/** @format */

import {ChangeEvent} from 'react';

import {Box, CircularProgress} from '@mui/material';
import {Trans, useTranslation} from 'react-i18next';

import {
  Body,
  StepperLabel,
  StepperRow,
  StepperButton,
  CountInput,
  PriceBlock,
  PriceCell,
  PriceDivider,
  PriceLabel,
  PriceValue,
  Footer,
  PayButtonGroup,
  PayButton,
} from './styles';

export interface PurchaseFormContentProps {
  count: number;
  countInputValue: string;
  unitPrice: number;
  loadingAction: 'card' | 'bank' | null;
  onCountChange: (raw: string) => void;
  onCountBlur: () => void;
  onIncrement: () => void;
  onDecrement: () => void;
  onCardClick: () => void;
  onBankClick: () => void;
}

const PurchaseFormContent = ({
  count,
  countInputValue,
  unitPrice,
  loadingAction,
  onCountChange,
  onCountBlur,
  onIncrement,
  onDecrement,
  onCardClick,
  onBankClick,
}: PurchaseFormContentProps) => {
  const {t} = useTranslation();
  const total = count * unitPrice;
  const isLoading = loadingAction !== null;

  return (
    <>
      <Body>
        <StepperLabel>
          <Trans>Укажите количество</Trans>
        </StepperLabel>

        <StepperRow>
          <StepperButton
            data-testid='count-decrement'
            aria-label={t('Уменьшить количество')}
            onClick={onDecrement}
            disabled={count <= 1 || isLoading}
          >
            <Box component='span' className='material-symbols-outlined' sx={{fontSize: '20px'}}>
              remove
            </Box>
          </StepperButton>
          <CountInput
            component='input'
            data-testid='count-input'
            aria-label={t('Количество исследований')}
            inputMode='numeric'
            value={countInputValue}
            onChange={(event: ChangeEvent<HTMLInputElement>) => onCountChange(event.target.value)}
            onBlur={onCountBlur}
            disabled={isLoading}
          />
          <StepperButton
            data-testid='count-increment'
            aria-label={t('Увеличить количество')}
            onClick={onIncrement}
            disabled={isLoading}
          >
            <Box component='span' className='material-symbols-outlined' sx={{fontSize: '20px'}}>
              add
            </Box>
          </StepperButton>
        </StepperRow>

        <PriceBlock>
          <PriceCell>
            <PriceLabel>
              <Trans>Стоимость исследования</Trans>
            </PriceLabel>
            <PriceValue>{unitPrice} ₽</PriceValue>
          </PriceCell>
          <PriceDivider />
          <PriceCell>
            <PriceLabel>
              <Trans>Сумма</Trans>
            </PriceLabel>
            <PriceValue>{total} ₽</PriceValue>
          </PriceCell>
        </PriceBlock>
      </Body>

      <Footer>
        <PayButtonGroup>
          <PayButton
            data-testid='pay-card'
            variant='contained'
            onClick={onCardClick}
            disabled={isLoading}
          >
            {loadingAction === 'card' ? (
              <CircularProgress size={20} sx={{color: 'inherit'}} />
            ) : (
              <Box component='span' className='material-symbols-outlined' sx={{fontSize: '20px'}}>
                credit_card
              </Box>
            )}
            <Box component='span'>{t('Оплатить картой')}</Box>
          </PayButton>

          <PayButton
            data-testid='pay-bank'
            variant='contained'
            onClick={onBankClick}
            disabled={isLoading}
          >
            {loadingAction === 'bank' ? (
              <CircularProgress size={20} sx={{color: 'inherit'}} />
            ) : (
              <Box component='span' className='material-symbols-outlined' sx={{fontSize: '20px'}}>
                receipt_long
              </Box>
            )}
            <Box component='span'>{t('Оплатить по счёту')}</Box>
          </PayButton>
        </PayButtonGroup>
      </Footer>
    </>
  );
};

export default PurchaseFormContent;
